import React, { useState, useEffect } from 'react';
import Login from './screens/Login';
import Landing from './screens/Landing';
import Manifesto from './screens/Manifesto';
import Privacy from './screens/Privacy';
import Terms from './screens/Terms';
import BrainLogo from './components/BrainLogo';
import API from './config';
import './App.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/mindris/id6804511282';

/**
 * mindris.app — an information site, plus two email landing pages.
 *
 * Mindris shipped on the App Store, so the web build no longer signs anyone in
 * or shows anyone their data. The dashboard, profile editor, situation reporter
 * and history screens are gone; they were scaffolding from before there was an
 * iOS app, and leaving a second, staler client running against the live
 * database is a liability rather than a feature.
 *
 * Two things survive, and they are load-bearing rather than legacy:
 *
 *   /?verify=TOKEN  — the backend mails this to every new account. It is the
 *                     only way an iOS user's email gets verified.
 *   /?reset=TOKEN   — likewise for password reset, including reset links
 *                     already sitting in people's inboxes.
 *
 * Both point at FRONTEND_URL in app/main.py, which is this site. Deleting them
 * would silently break signup and account recovery for every mobile user, and
 * the failure would look like the app being broken rather than the website.
 */
export default function App() {
  const [screen, setScreen] = useState('landing');
  const [resetToken, setResetToken] = useState(null);

  useEffect(() => {
    // Legal pages are reachable by URL. app.json points the iOS app at
    // /privacy, and App Store review follows that link.
    const path = window.location.pathname.replace(/\/+$/, '');
    if (path === '/privacy') { setScreen('privacy'); return; }
    if (path === '/terms') { setScreen('terms'); return; }

    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get('verify');
    const pwResetToken = params.get('reset');

    if (pwResetToken) {
      // Clean the URL so the token doesn't sit in the address bar or get
      // copied into a support email.
      window.history.replaceState({}, '', '/');
      setResetToken(pwResetToken);
      setScreen('reset');
      return;
    }

    if (verifyToken) {
      window.history.replaceState({}, '', '/');
      setScreen('verifying');
      fetch(`${API}/auth/verify-email?token=${verifyToken}`)
        .then((r) => r.json())
        .then((data) => {
          // The endpoint returns a token on success. We deliberately don't
          // store it — there's nothing on the web to sign into. All the user
          // needs to know is that it worked and to go back to the app.
          setScreen(data.access_token ? 'verified' : 'verify-failed');
        })
        .catch(() => setScreen('verify-failed'));
    }
  }, []);

  const navigate = (s) => {
    const path = s === 'privacy' ? '/privacy' : s === 'terms' ? '/terms' : '/';
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
    setScreen(s);
  };

  if (screen === 'verifying') {
    return (
      <Centered>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15 }}>
          Verifying your email…
        </div>
      </Centered>
    );
  }

  if (screen === 'verified') {
    return (
      <Centered>
        <h1 style={styles.title}>Email verified.</h1>
        <p style={styles.body}>
          You're all set. Open Mindris on your phone and sign in.
        </p>
        <a href={APP_STORE_URL} style={styles.primary}>Open the App Store</a>
      </Centered>
    );
  }

  if (screen === 'verify-failed') {
    return (
      <Centered>
        <h1 style={styles.title}>That link didn't work.</h1>
        <p style={styles.body}>
          Verification links expire. Open Mindris on your phone and request a
          new one, or email tylerkim1020@gmail.com and we'll sort it out.
        </p>
        <button onClick={() => navigate('landing')} style={styles.ghost}>
          ← Back to Mindris
        </button>
      </Centered>
    );
  }

  // Password reset, reached only from an emailed link.
  if (screen === 'reset' && resetToken) {
    return <Login resetToken={resetToken} onLogin={() => setScreen('reset-done')} />;
  }

  if (screen === 'reset-done') {
    return (
      <Centered>
        <h1 style={styles.title}>Password changed.</h1>
        <p style={styles.body}>
          Open Mindris on your phone and sign in with your new password.
        </p>
        <a href={APP_STORE_URL} style={styles.primary}>Open the App Store</a>
      </Centered>
    );
  }

  if (screen === 'manifesto') return <Manifesto navigate={navigate} />;
  if (screen === 'privacy') return <Privacy navigate={navigate} />;
  if (screen === 'terms') return <Terms navigate={navigate} />;

  return <Landing navigate={navigate} />;
}

/** Shared shell for the four one-line email landing states. */
function Centered({ children }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: '#0a0a0a', gap: 18, padding: '0 28px', textAlign: 'center',
      fontFamily: 'Inter, sans-serif',
    }}>
      <BrainLogo size={40} color="white" />
      {children}
    </div>
  );
}

const styles = {
  title: {
    color: '#ffffff', fontSize: 26, fontWeight: 700,
    letterSpacing: '-0.6px', margin: 0,
    fontFamily: 'Lora, Georgia, serif',
  },
  body: {
    color: 'rgba(255,255,255,0.5)', fontSize: 15, lineHeight: 1.7,
    maxWidth: 380, margin: 0,
  },
  primary: {
    background: '#ffffff', color: '#0a0a0a', textDecoration: 'none',
    borderRadius: 999, padding: '13px 32px', fontSize: 15, fontWeight: 600,
    marginTop: 6,
  },
  ghost: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#6b6b6b', fontSize: 14, fontFamily: 'Inter, sans-serif',
    marginTop: 6,
  },
};
